import { ReactElement, ReactNode } from 'react';
import { Heading, ItemLoader, Page, useEntity } from 'components';
import { Alert, Button, Dialog, DialogContent, DialogTrigger, Stack } from '@ff-it/ui';
import { createStateTag } from 'components/StateTag';
import type { KeyedMutator } from 'swr';
import type { Enterprise } from 'modules/core/types';
import { formatDateTime, humanizeName } from 'utilities';
import { ActionButton } from './ActionButton';
import { EditDialog } from './EditDialog';
import { editDialogStyle } from './style.css';
import type { Integration, IntegrationState } from './types';

export const IntegrationStateTag = createStateTag<IntegrationState>({
  testId: 'bigquery-state-tag',
  stateVariantMap: {
    READY: { icon: 'circle-check', variant: 'success' },
    DISABLED: { icon: 'pause', variant: 'secondary' },
    NOT_CONFIGURED: { icon: 'gear', variant: 'secondary' },
  },
  displayTransform: humanizeName,
});

function Row({ label, value }: { label: string; value: ReactNode }): ReactElement {
  return (
    <div className="row mb-1">
      <div className="col-4 text-muted">{label}</div>
      <div className="col-8">{value ?? <span className="text-muted">—</span>}</div>
    </div>
  );
}

function Toolbar({
  baseUrl,
  integration,
  mutate,
}: {
  baseUrl: string;
  integration: Integration;
  mutate: KeyedMutator<Integration>;
}): ReactElement {
  const showTest = integration.state !== 'NOT_CONFIGURED';
  const showDisable = integration.state === 'READY';
  const showEnable = integration.state === 'DISABLED';
  const showDeleteConfiguration = integration.state === 'DISABLED';

  return (
    <Stack direction="horizontal" space="2">
      {showTest && (
        <ActionButton
          url={`${baseUrl}test/`}
          label="Test connection"
          icon="plug"
          successMessage="Connection OK."
          mutate={mutate}
        />
      )}
      {showDisable && (
        <ActionButton
          url={`${baseUrl}disable/`}
          label="Disable"
          icon="pause"
          successMessage="Disabled."
          confirm={{
            title: 'Disable BigQuery integration?',
            body: 'Sync will stop for this agency. Existing rows in BigQuery will be left in place. Credentials are kept so you can re-enable later.',
          }}
          mutate={mutate}
        />
      )}
      {showEnable && (
        <ActionButton
          url={`${baseUrl}enable/`}
          label="Enable"
          icon="play"
          variant="primary"
          successMessage="Enabled."
          mutate={mutate}
        />
      )}
      {showDeleteConfiguration && (
        <ActionButton
          url={baseUrl}
          method="DELETE"
          label="Delete configuration"
          icon="trash"
          variant="danger"
          successMessage="Configuration has been deleted."
          confirm={{
            title: 'Delete BigQuery configuration?',
            body: 'The stored project, dataset and service account credentials will all be removed. Existing rows in BigQuery will be left in place, but the app will no longer have access to the dataset.',
          }}
          mutate={mutate}
        />
      )}
      <Dialog>
        <DialogTrigger>
          <Button icon="pencil" size="sm" variant="secondary">
            Edit
          </Button>
        </DialogTrigger>
        <DialogContent className={editDialogStyle}>
          <EditDialog baseUrl={baseUrl} integration={integration} mutate={mutate} />
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

function View({
  baseUrl,
  integration,
  mutate,
}: {
  baseUrl: string;
  integration: Integration;
  mutate: KeyedMutator<Integration>;
}): ReactElement {
  const target =
    integration.project_id && integration.dataset_id ? `${integration.project_id}.${integration.dataset_id}` : null;
  return (
    <Page className="scene">
      <Heading title="BigQuery">
        <Toolbar baseUrl={baseUrl} integration={integration} mutate={mutate} />
      </Heading>

      {integration.last_error ? (
        <Alert variant="danger" heading="Last error" mb="3">
          {integration.last_error}
        </Alert>
      ) : null}

      <Row label="State" value={<IntegrationStateTag state={integration.state} />} />
      <Row label="Project / Dataset" value={target} />
      <Row label="Service account" value={integration.credentials_client_email} />
      <Row label="Key id" value={integration.credentials_private_key_id} />
      <Row label="Last sync" value={integration.last_success_at ? formatDateTime(integration.last_success_at) : null} />
    </Page>
  );
}

export function BigQueryIntegration(): ReactElement {
  const { item } = useEntity<Enterprise>();
  const url = `bigquery/${item.id}/`;

  return (
    <ItemLoader<Integration> url={url}>
      {({ item: integration, mutate }) => <View baseUrl={url} integration={integration} mutate={mutate} />}
    </ItemLoader>
  );
}
